import React, { useContext, useEffect, useState } from 'react';
import { RoomLeftState } from '../types/dyte-client';
import { defaultConfig } from '../lib/default-ui-config';
import { Render } from '../lib/render';
import { UIConfig } from '../types/ui-config';
import DyteClient, {
  DyteThemePreset,
  SocketConnectionState,
} from '@dytesdk/web-core';
import { DyteI18n, useLanguage } from '../lib/lang';
import { extendConfig } from '../utils/config';
import { getIconPack } from '../lib/icons';
import { Platform } from 'react-native';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  applyDesignSystem = true,
  config = defaultConfig,
  iconPackUrl = '',
  showSetupScreen = true,
  iOSScreenshareEnabled = false,
  t,
}: {
  meeting: DyteClient;
  applyDesignSystem?: boolean;
  config?: UIConfig;
  iconPackUrl?: string;
  showSetupScreen?: boolean;
  iOSScreenshareEnabled?: boolean;
  t?: DyteI18n;
}) {
  const { storeStates, setStates } = useContext(DyteUIContext);
  const getPresetColors = (_config: DyteThemePreset) => {
    return (meeting.self.config as DyteThemePreset).designTokens.colors;
  };
  const [iconPack, setIconPack] = useState(null);
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  const colors = storeStates.designSystem.colors;
  const hideScreenshare = Platform.OS === 'ios' && !iOSScreenshareEnabled;
  const customConfig = hideScreenshare
    ? extendConfig(
        {
          styles: {
            'dyte-header.sm': {
              backgroundColor: colors.background[800],
            },
            'dyte-controlbar.sm': {
              backgroundColor: colors.background[800],
            },
          },
          root: {
            'div#more-menu': [
              ['dyte-mute-all', { variant: 'horizontal' }],
              ['dyte-chat-toggle', { variant: 'horizontal' }],
              ['dyte-polls-toggle', { variant: 'horizontal' }],
              ['dyte-participants-toggle', { variant: 'horizontal' }],
              ['dyte-recording-toggle', { variant: 'horizontal' }],
              ['dyte-fullscreen-toggle', { variant: 'horizontal' }],
              ['dyte-plugins-toggle', { variant: 'horizontal' }],
              ['dyte-settings-toggle', { variant: 'horizontal' }],
            ],
          },
        },
        config
      )
    : extendConfig(
        {
          styles: {
            'dyte-header.sm': {
              backgroundColor: colors.background[800],
            },
            'dyte-controlbar.sm': {
              backgroundColor: colors.background[800],
            },
          },
        },
        config
      );
  useEffect(() => {
    const setupScreenInit = async () => {
      if (!meeting.self.roomJoined) {
        if (showSetupScreen) {
          setStates({
            ...storeStates,
            meeting: 'setup',
          });
        } else {
          await meeting.joinRoom();
        }
      }
    };
    setupScreenInit();
    if (meeting.self.roomJoined) {
      setStates({
        ...storeStates,
        meeting: 'joined',
      });
    }

    const roomJoinedListener = () => {
      setStates({ ...storeStates, meeting: 'joined', roomLeftState: '' });
    };

    // const livestreamRoomJoinedListener = () => {
    //   if (meeting?.meta.viewType === 'LIVESTREAM') {
    //     setStates({ ...storeStates, meeting: 'joined' });
    //   }
    // };

    const roomLeftListener = ({ state }: { state: RoomLeftState }) => {
      if (state === 'kicked' || state === 'rejected') {
        setStates({
          ...storeStates,
          meeting: 'waiting',
          roomLeftState: state,
          activeFullscreen: false,
          activeMoreMenu: false,
          activeSettings: false,
          activeSidebar: false,
          activeLeaveConfirmation: false,
          activeStageConfirmation: false,
          activeJoinStageConfirmation: false,
        });
      } else if (state === 'ended' || state === 'left') {
        setStates({
          ...storeStates,
          meeting: 'ended',
          roomLeftState: 'ended',
          activeFullscreen: false,
          activeMoreMenu: false,
          activeSettings: false,
          activeSidebar: false,
          activeLeaveConfirmation: false,
          activeStageConfirmation: false,
          activeJoinStageConfirmation: false,
        });
      }
    };
    const socketFailureHandler = () => {
      setStates({
        ...storeStates,
        meeting: 'waiting',
        roomLeftState: 'disconnected',
        activeFullscreen: false,
        activeMoreMenu: false,
        activeSettings: false,
        activeSidebar: false,
        activeLeaveConfirmation: false,
        activeStageConfirmation: false,
        activeJoinStageConfirmation: false,
      });
    };
    const socketHandler = (status: SocketConnectionState) => {
      if (status.state === 'failed') socketFailureHandler();
    };
    meeting.meta.on('socketConnectionUpdate', socketHandler);
    meeting?.self.addListener('roomJoined', roomJoinedListener);
    meeting?.self.addListener('roomLeft', roomLeftListener);
    // meeting?.self.addListener(
    //   'socketServiceRoomJoined',
    //   livestreamRoomJoinedListener
    // );
    return () => {
      meeting.meta.off('socketConnectionUpdate', socketHandler);
      meeting?.self.removeListener('roomJoined', roomJoinedListener);
      meeting?.self.removeListener('roomLeft', roomLeftListener);
      // meeting?.self.removeListener(
      //   'socketServiceRoomJoined',
      //   livestreamRoomJoinedListener
      // );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting, colors]);
  useEffect(() => {
    if (applyDesignSystem)
      setStates({
        ...storeStates,
        designSystem: {
          ...storeStates.designSystem,
          colors: {
            ...storeStates.designSystem.colors,
            ...getPresetColors(meeting.self.config),
          },
        },
      });
    if (iconPackUrl.trim().length !== 0) {
      const _icons = getIconPack(iconPackUrl);
      setIconPack(_icons);
    }
    return () => {
      setStates({
        ...storeStates,
        meeting: 'waiting',
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Render
      element="dyte-meeting"
      defaults={{
        config: customConfig,
        meeting,
        states: storeStates,
        size: 'sm',
        iconPack: iconPack,
        t: t,
      }}
    />
  );
}
